import {
  findWeatherLayerByUrl,
  normalizeWeatherServiceUrl,
  WEATHER_FEATURESERVER_CATALOG,
  type WeatherFeatureServerLayer,
} from 'components/MapPage/CorporateViewTabs/EventsTab/weatherFeatureServerCatalog';
import type { SelectedLayerForAI } from './prefetchLayerFeaturesForAI';

/** Living Atlas Web Map filed with Tropical FeatureServer hurricane rows. */
const ACTIVE_HURRICANES_GLOBAL_ITEM_ID = '248e7b5827a34b248647afb012c58787';
const ACTIVE_HURRICANES_GLOBAL_NAME =
  'Active Hurricanes / Tropical Cyclones (Global)';
const WILDFIRE_INCIDENTS_CATALOG_ID = 'wildfire-incidents';
const WILDFIRE_INCIDENTS_NAME = 'Current wildfire incidents';
const WILDFIRE_INCIDENTS_URL =
  'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/USA_Wildfires_v1/FeatureServer/0';

export type AiLayerCatalogExternal = SelectedLayerForAI & {
  /** Weather catalog category when this URL is a known NOAA/NWS layer. */
  category?: string;
};

export type ExternalLayerForAiCatalog = {
  id: string;
  name?: string | null;
  serviceUrl?: string | null;
  arcgisItemId?: string | null;
  incidentId?: string | null;
};

/**
 * Union org ArcGIS external layers with the public weather FeatureServer
 * catalog. Prefer the live `external_layers` UUID when a catalog URL is already
 * seeded; catalog id (e.g. ndfd-precipitation-total) is fallback only.
 */
export function buildAiLayerCatalog(params: {
  apiLayers: ExternalLayerForAiCatalog[];
  weatherCatalog?: WeatherFeatureServerLayer[];
}): AiLayerCatalogExternal[] {
  const weatherCatalog = params.weatherCatalog ?? WEATHER_FEATURESERVER_CATALOG;
  const seenIds = new Set<string>();
  const seenArcgisItemIds = new Set<string>();
  const seenServiceUrls = new Set<string>();
  const items: AiLayerCatalogExternal[] = [];

  const sorted = [...params.apiLayers].sort(
    (a, b) => Number(!!a.incidentId) - Number(!!b.incidentId)
  );

  for (const layer of sorted) {
    const id = String(layer.id);
    if (seenIds.has(id)) continue;
    const itemId = layer.arcgisItemId ?? null;
    if (itemId && seenArcgisItemIds.has(itemId)) continue;
    seenIds.add(id);
    if (itemId) seenArcgisItemIds.add(itemId);
    const serviceUrl = layer.serviceUrl ?? null;
    if (serviceUrl) {
      seenServiceUrls.add(normalizeWeatherServiceUrl(serviceUrl));
    }
    const weather = findWeatherLayerByUrl(serviceUrl);
    const isActiveHurricanesGlobal =
      itemId === ACTIVE_HURRICANES_GLOBAL_ITEM_ID;
    const category = isActiveHurricanesGlobal ? 'Tropical' : weather?.category;
    items.push({
      kind: 'external',
      id,
      name: layer.name || weather?.name || 'Untitled external layer',
      serviceUrl,
      arcgisItemId: layer.arcgisItemId,
      ...(category ? { category } : {}),
    });
  }

  for (const entry of weatherCatalog) {
    const url = normalizeWeatherServiceUrl(entry.serviceUrl);
    if (seenServiceUrls.has(url)) continue;
    seenServiceUrls.add(url);
    items.push({
      kind: 'external',
      id: entry.id,
      name: entry.name,
      serviceUrl: entry.serviceUrl,
      arcgisItemId: null,
      category: entry.category,
    });
  }

  return items;
}

export function filterAiLayerCatalog<T extends { name?: string }>(
  items: T[],
  searchTerm: string
): T[] {
  const q = searchTerm.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) => (item.name ?? '').toLowerCase().includes(q));
}

export function groupAiLayersByCategory(
  items: AiLayerCatalogExternal[]
): { label: string; items: AiLayerCatalogExternal[] }[] {
  const groups: { label: string; items: AiLayerCatalogExternal[] }[] = [];
  const indexByLabel = new Map<string, number>();
  for (const item of items) {
    const label = item.category?.trim();
    if (!label) continue;
    const existing = indexByLabel.get(label);
    if (existing != null) {
      groups[existing].items.push(item);
      continue;
    }
    indexByLabel.set(label, groups.length);
    groups.push({ label, items: [item] });
  }
  return groups;
}

/**
 * Analytics sections can only pick Current wildfire incidents and the global
 * Active Hurricanes web map. Other chat pickers keep the full catalog.
 */
export function isAnalyticsSelectableLayer(
  layer: Pick<
    SelectedLayerForAI,
    'kind' | 'id' | 'name' | 'serviceUrl' | 'arcgisItemId'
  >
): boolean {
  if (layer.kind !== 'external') return false;
  const name = layer.name?.trim().toLowerCase() ?? '';
  if (
    layer.id === WILDFIRE_INCIDENTS_CATALOG_ID ||
    name === WILDFIRE_INCIDENTS_NAME.toLowerCase()
  ) {
    return true;
  }
  if (
    layer.serviceUrl &&
    normalizeWeatherServiceUrl(layer.serviceUrl) ===
      normalizeWeatherServiceUrl(WILDFIRE_INCIDENTS_URL)
  ) {
    return true;
  }
  return (
    layer.id === ACTIVE_HURRICANES_GLOBAL_ITEM_ID ||
    layer.arcgisItemId === ACTIVE_HURRICANES_GLOBAL_ITEM_ID ||
    name === ACTIVE_HURRICANES_GLOBAL_NAME.toLowerCase()
  );
}

export function sameExternalLayerForAi(
  a: Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>,
  b: Pick<SelectedLayerForAI, 'kind' | 'id' | 'serviceUrl'>
): boolean {
  if (a.kind !== b.kind) return false;
  if (a.id === b.id) return true;
  if (a.kind !== 'external') return false;
  if (!a.serviceUrl || !b.serviceUrl) return false;
  return (
    normalizeWeatherServiceUrl(a.serviceUrl) ===
    normalizeWeatherServiceUrl(b.serviceUrl)
  );
}
